import type { BorderEdge as StandaloneGradeBorderEdge } from '../../lib/types'
import {
  evaluateCenteringCurve,
  getCenteringCurveDerivative,
  hasCenteringCurveMidpoint,
  isValidCenteringCurve,
  sampleCenteringCurve,
  trimCenteringCurve,
  type CenteringCurveLike,
} from './advancedCenteringCurve'

export type CenteringBoundaryPoint = {
  x: number
  y: number
}

export type CenteringOuterBoundary = {
  points: [
    CenteringBoundaryPoint,
    CenteringBoundaryPoint,
    CenteringBoundaryPoint,
    CenteringBoundaryPoint,
  ]
  /**
   * Canonically oriented, corner-trimmed sides. Left/right run top-to-bottom;
   * top/bottom run left-to-right.
   */
  sides: Record<StandaloneGradeBorderEdge, CenteringCurveLike>
  /** Sampled side geometry for curved SVG paths, clipping, and border bands. */
  sidePoints: Record<StandaloneGradeBorderEdge, CenteringBoundaryPoint[]>
}

export type CenteringBorderBands = Record<StandaloneGradeBorderEdge, CenteringBoundaryPoint[]>

export type CenteringBorderGeometry = {
  outerBoundary: CenteringOuterBoundary | null
  innerBoundary: CenteringOuterBoundary | null
  bands: CenteringBorderBands | null
}

type CenteringLineKey = 'outer_line' | 'inner_line'

type CenteringLineSource = Partial<
  Record<
    StandaloneGradeBorderEdge,
    {
      outer_line?: CenteringCurveLike | null
      inner_line?: CenteringCurveLike | null
    }
  >
>

export type CenteringProjectionSideSource = {
  available?: boolean
  outer_line?: CenteringCurveLike | null
  inner_line?: CenteringCurveLike | null
}

type CurveIntersection = {
  point: CenteringBoundaryPoint
  firstParameter: number
  secondParameter: number
}

const GEOMETRY_EPSILON = 1e-9
const CURVE_INTERSECTION_SAMPLES = 32
const CURVE_SIDE_SAMPLES = 16
const INTERSECTION_PARAMETER_MIN = -0.25
const INTERSECTION_PARAMETER_MAX = 1.25

/**
 * SVG clips paths to its viewport. Keep a projected border when any sampled
 * portion crosses the image instead of requiring both transformed endpoints
 * to remain inside it.
 */
export function isCenteringCurveVisibleInImage(
  curve: CenteringCurveLike | null | undefined,
  width: number,
  height: number,
): boolean {
  if (!isValidCenteringCurve(curve) || width <= 0 || height <= 0) return false

  const points = sampleCenteringCurve(curve, 64)
  return points.some((point, index) => {
    if (pointInImage(point, width, height)) return true
    const next = points[index + 1]
    return Boolean(next && segmentIntersectsImage(point, next, width, height))
  })
}

/**
 * A complete V2 fit can extrapolate past the source raster while both detected
 * boundaries still cross it. Keep that side renderable; the SVG viewport will
 * clip only the offscreen portions.
 */
export function isCenteringProjectionSideRenderable(
  side: CenteringProjectionSideSource | null | undefined,
  width: number,
  height: number,
): boolean {
  return Boolean(
    side?.available &&
    isCenteringCurveVisibleInImage(side.outer_line, width, height) &&
    isCenteringCurveVisibleInImage(side.inner_line, width, height),
  )
}

export function resolveCenteringProjectionDisplaySide(
  projectedSide: CenteringProjectionSideSource | null | undefined,
  completeDisplaySide: CenteringProjectionSideSource | null | undefined,
  hasCompleteDisplayBoundaries: boolean,
  width: number,
  height: number,
): CenteringProjectionSideSource | null {
  if (hasCompleteDisplayBoundaries && completeDisplaySide) return completeDisplaySide
  return isCenteringProjectionSideRenderable(projectedSide, width, height)
    ? (projectedSide ?? null)
    : null
}

export function buildCenteringOuterBoundary(
  sides: CenteringLineSource | null | undefined,
): CenteringOuterBoundary | null {
  return buildCenteringBoundary(sides, 'outer_line')
}

export function buildCenteringBorderGeometry(
  sides: CenteringLineSource | null | undefined,
): CenteringBorderGeometry {
  const outerBoundary = buildCenteringBoundary(sides, 'outer_line')
  const innerBoundary = buildCenteringBoundary(sides, 'inner_line')
  const bands =
    outerBoundary && innerBoundary ? buildBorderBands(outerBoundary, innerBoundary) : null

  return { outerBoundary, innerBoundary, bands }
}

function buildBorderBands(
  outerBoundary: CenteringOuterBoundary,
  innerBoundary: CenteringOuterBoundary,
): CenteringBorderBands | null {
  const outerPolygon = boundaryPolygonPoints(outerBoundary)
  const innerPolygon = boundaryPolygonPoints(innerBoundary)
  const outerArea = signedCenteringPolygonArea(outerPolygon)
  const innerArea = signedCenteringPolygonArea(innerPolygon)
  if (
    !isBoundaryContained(innerPolygon, outerPolygon) ||
    Math.sign(outerArea) !== Math.sign(innerArea) ||
    Math.abs(innerArea) >= Math.abs(outerArea)
  ) {
    return null
  }

  const bands: CenteringBorderBands = {
    top: [...outerBoundary.sidePoints.top, ...[...innerBoundary.sidePoints.top].reverse()],
    right: [...outerBoundary.sidePoints.right, ...[...innerBoundary.sidePoints.right].reverse()],
    bottom: [...[...outerBoundary.sidePoints.bottom].reverse(), ...innerBoundary.sidePoints.bottom],
    left: [...[...outerBoundary.sidePoints.left].reverse(), ...innerBoundary.sidePoints.left],
  }

  return Object.values(bands).every(
    (band) =>
      isSimplePolygon(band) &&
      Math.abs(signedCenteringPolygonArea(band)) > GEOMETRY_EPSILON &&
      Math.sign(signedCenteringPolygonArea(band)) === Math.sign(outerArea) &&
      (band.length !== 4 ||
        isConvexQuadrilateral(
          band as [
            CenteringBoundaryPoint,
            CenteringBoundaryPoint,
            CenteringBoundaryPoint,
            CenteringBoundaryPoint,
          ],
        )),
  )
    ? bands
    : null
}

function buildCenteringBoundary(
  sides: CenteringLineSource | null | undefined,
  lineKey: CenteringLineKey,
): CenteringOuterBoundary | null {
  const left = sides?.left?.[lineKey]
  const right = sides?.right?.[lineKey]
  const top = sides?.top?.[lineKey]
  const bottom = sides?.bottom?.[lineKey]
  if (
    !isValidCenteringCurve(left) ||
    !isValidCenteringCurve(right) ||
    !isValidCenteringCurve(top) ||
    !isValidCenteringCurve(bottom)
  ) {
    return null
  }

  const topLeft = intersectCurves(left, top, 0, 0)
  const topRight = intersectCurves(right, top, 0, 1)
  const bottomRight = intersectCurves(right, bottom, 1, 1)
  const bottomLeft = intersectCurves(left, bottom, 1, 0)
  if (!topLeft || !topRight || !bottomRight || !bottomLeft) return null

  const points: CenteringOuterBoundary['points'] = [
    topLeft.point,
    topRight.point,
    bottomRight.point,
    bottomLeft.point,
  ]
  if (!isConvexQuadrilateral(points)) return null

  const trimmedSides: CenteringOuterBoundary['sides'] = {
    left: trimBoundarySide(
      left,
      topLeft.firstParameter,
      bottomLeft.firstParameter,
      topLeft.point,
      bottomLeft.point,
    ),
    right: trimBoundarySide(
      right,
      topRight.firstParameter,
      bottomRight.firstParameter,
      topRight.point,
      bottomRight.point,
    ),
    top: trimBoundarySide(
      top,
      topLeft.secondParameter,
      topRight.secondParameter,
      topLeft.point,
      topRight.point,
    ),
    bottom: trimBoundarySide(
      bottom,
      bottomLeft.secondParameter,
      bottomRight.secondParameter,
      bottomLeft.point,
      bottomRight.point,
    ),
  }
  const sidePoints: CenteringOuterBoundary['sidePoints'] = {
    left: sampleBoundarySide(trimmedSides.left),
    right: sampleBoundarySide(trimmedSides.right),
    top: sampleBoundarySide(trimmedSides.top),
    bottom: sampleBoundarySide(trimmedSides.bottom),
  }
  const boundary: CenteringOuterBoundary = { points, sides: trimmedSides, sidePoints }
  const polygon = boundaryPolygonPoints(boundary)
  if (
    !isSimplePolygon(polygon) ||
    Math.abs(signedCenteringPolygonArea(polygon)) <= GEOMETRY_EPSILON
  ) {
    return null
  }
  return boundary
}

function trimBoundarySide(
  curve: CenteringCurveLike,
  startParameter: number,
  endParameter: number,
  start: CenteringBoundaryPoint,
  end: CenteringBoundaryPoint,
): CenteringCurveLike {
  if (!hasCenteringCurveMidpoint(curve)) return lineFromPoints(start, end)

  const trimmed = trimCenteringCurve(curve, startParameter, endParameter)
  return {
    ...trimmed,
    x1: start.x,
    y1: start.y,
    x2: end.x,
    y2: end.y,
  }
}

function sampleBoundarySide(curve: CenteringCurveLike) {
  if (!hasCenteringCurveMidpoint(curve)) {
    return [
      { x: curve.x1, y: curve.y1 },
      { x: curve.x2, y: curve.y2 },
    ]
  }
  const points = sampleCenteringCurve(curve, CURVE_SIDE_SAMPLES)
  points[0] = { x: curve.x1, y: curve.y1 }
  points[points.length - 1] = { x: curve.x2, y: curve.y2 }
  return points
}

function boundaryPolygonPoints(boundary: CenteringOuterBoundary) {
  return joinPointRuns([
    boundary.sidePoints.top,
    boundary.sidePoints.right,
    [...boundary.sidePoints.bottom].reverse(),
    [...boundary.sidePoints.left].reverse(),
  ])
}

function joinPointRuns(runs: readonly CenteringBoundaryPoint[][]) {
  const points: CenteringBoundaryPoint[] = []
  for (const run of runs) {
    for (const point of run) {
      const previous = points[points.length - 1]
      if (!previous || !pointsEqual(previous, point)) points.push(point)
    }
  }
  if (points.length > 1 && pointsEqual(points[0], points[points.length - 1])) points.pop()
  return points
}

function isBoundaryContained(
  inner: readonly CenteringBoundaryPoint[],
  outer: readonly CenteringBoundaryPoint[],
) {
  return inner.every((point) => isPointInsidePolygon(point, outer))
}

function isPointInsidePolygon(
  point: CenteringBoundaryPoint,
  polygon: readonly CenteringBoundaryPoint[],
) {
  let inside = false
  for (
    let index = 0, previousIndex = polygon.length - 1;
    index < polygon.length;
    previousIndex = index++
  ) {
    const start = polygon[previousIndex]
    const end = polygon[index]
    if (pointOnSegment(point, start, end)) return true
    const crosses =
      (start.y > point.y) !== (end.y > point.y) &&
      point.x < ((end.x - start.x) * (point.y - start.y)) / (end.y - start.y) + start.x
    if (crosses) inside = !inside
  }
  return inside
}

export function signedCenteringPolygonArea(points: readonly CenteringBoundaryPoint[]) {
  return (
    points.reduce((sum, point, index) => {
      const next = points[(index + 1) % points.length]
      return sum + point.x * next.y - next.x * point.y
    }, 0) / 2
  )
}

function intersectCurves(
  first: CenteringCurveLike,
  second: CenteringCurveLike,
  preferredFirstParameter: number,
  preferredSecondParameter: number,
): CurveIntersection | null {
  if (!hasCenteringCurveMidpoint(first) && !hasCenteringCurveMidpoint(second)) {
    return intersectInfiniteLines(first, second)
  }

  // Valid card borders are mild, monotonic bends. Their expected corner is
  // therefore reached directly from the matching endpoint seed in nearly all
  // cases, avoiding a sampled O(n²) search during every drag frame.
  const preferred = refineCurveIntersection(
    first,
    second,
    preferredFirstParameter,
    preferredSecondParameter,
  )
  if (preferred) return preferred

  const firstSamples = parameterizedSamples(first)
  const secondSamples = parameterizedSamples(second)
  const candidates: CurveIntersection[] = []
  for (let firstIndex = 0; firstIndex < firstSamples.length - 1; firstIndex += 1) {
    const firstStart = firstSamples[firstIndex]
    const firstEnd = firstSamples[firstIndex + 1]
    for (let secondIndex = 0; secondIndex < secondSamples.length - 1; secondIndex += 1) {
      const secondStart = secondSamples[secondIndex]
      const secondEnd = secondSamples[secondIndex + 1]
      const crossing = intersectLineSegments(
        firstStart.point,
        firstEnd.point,
        secondStart.point,
        secondEnd.point,
      )
      if (!crossing) continue
      const firstParameter =
        firstStart.parameter + (firstEnd.parameter - firstStart.parameter) * crossing.firstProgress
      const secondParameter =
        secondStart.parameter +
        (secondEnd.parameter - secondStart.parameter) * crossing.secondProgress
      const refined = refineCurveIntersection(first, second, firstParameter, secondParameter)
      if (refined) candidates.push(refined)
    }
  }

  if (candidates.length === 0) return null

  return candidates.reduce((best, candidate) =>
    intersectionPreferenceDistance(candidate, preferredFirstParameter, preferredSecondParameter) <
    intersectionPreferenceDistance(best, preferredFirstParameter, preferredSecondParameter)
      ? candidate
      : best,
  )
}

function parameterizedSamples(curve: CenteringCurveLike) {
  return Array.from({ length: CURVE_INTERSECTION_SAMPLES + 1 }, (_, index) => {
    const parameter =
      INTERSECTION_PARAMETER_MIN +
      ((INTERSECTION_PARAMETER_MAX - INTERSECTION_PARAMETER_MIN) * index) /
        CURVE_INTERSECTION_SAMPLES
    return { parameter, point: evaluateCenteringCurve(curve, parameter) }
  })
}

function refineCurveIntersection(
  first: CenteringCurveLike,
  second: CenteringCurveLike,
  initialFirstParameter: number,
  initialSecondParameter: number,
): CurveIntersection | null {
  let firstParameter = initialFirstParameter
  let secondParameter = initialSecondParameter

  for (let iteration = 0; iteration < 16; iteration += 1) {
    const firstPoint = evaluateCenteringCurve(first, firstParameter)
    const secondPoint = evaluateCenteringCurve(second, secondParameter)
    const difference = {
      x: firstPoint.x - secondPoint.x,
      y: firstPoint.y - secondPoint.y,
    }
    if (Math.hypot(difference.x, difference.y) <= 1e-8) break

    const firstDerivative = getCenteringCurveDerivative(first, firstParameter)
    const secondDerivative = getCenteringCurveDerivative(second, secondParameter)
    const a = firstDerivative.x
    const b = -secondDerivative.x
    const c = firstDerivative.y
    const d = -secondDerivative.y
    const determinant = a * d - b * c
    if (Math.abs(determinant) <= GEOMETRY_EPSILON) return null

    const firstStep = (-difference.x * d + b * difference.y) / determinant
    const secondStep = (-a * difference.y + difference.x * c) / determinant
    firstParameter = clamp(
      firstParameter + firstStep,
      INTERSECTION_PARAMETER_MIN,
      INTERSECTION_PARAMETER_MAX,
    )
    secondParameter = clamp(
      secondParameter + secondStep,
      INTERSECTION_PARAMETER_MIN,
      INTERSECTION_PARAMETER_MAX,
    )
  }

  const firstPoint = evaluateCenteringCurve(first, firstParameter)
  const secondPoint = evaluateCenteringCurve(second, secondParameter)
  if (
    !finitePoint(firstPoint) ||
    !finitePoint(secondPoint) ||
    Math.hypot(firstPoint.x - secondPoint.x, firstPoint.y - secondPoint.y) > 1e-5
  ) {
    return null
  }
  return {
    point: {
      x: (firstPoint.x + secondPoint.x) / 2,
      y: (firstPoint.y + secondPoint.y) / 2,
    },
    firstParameter,
    secondParameter,
  }
}

function intersectionPreferenceDistance(
  intersection: CurveIntersection,
  preferredFirstParameter: number,
  preferredSecondParameter: number,
) {
  return (
    Math.abs(intersection.firstParameter - preferredFirstParameter) +
    Math.abs(intersection.secondParameter - preferredSecondParameter)
  )
}

function intersectInfiniteLines(
  first: CenteringCurveLike,
  second: CenteringCurveLike,
): CurveIntersection | null {
  const firstVector = {
    x: first.x2 - first.x1,
    y: first.y2 - first.y1,
  }
  const secondVector = {
    x: second.x2 - second.x1,
    y: second.y2 - second.y1,
  }
  const denominator = cross(firstVector, secondVector)
  if (Math.abs(denominator) < GEOMETRY_EPSILON) return null

  const betweenStarts = {
    x: second.x1 - first.x1,
    y: second.y1 - first.y1,
  }
  const firstParameter = cross(betweenStarts, secondVector) / denominator
  const secondParameter = cross(betweenStarts, firstVector) / denominator
  const point = {
    x: first.x1 + firstVector.x * firstParameter,
    y: first.y1 + firstVector.y * firstParameter,
  }
  return finitePoint(point) ? { point, firstParameter, secondParameter } : null
}

function intersectLineSegments(
  firstStart: CenteringBoundaryPoint,
  firstEnd: CenteringBoundaryPoint,
  secondStart: CenteringBoundaryPoint,
  secondEnd: CenteringBoundaryPoint,
) {
  const firstVector = {
    x: firstEnd.x - firstStart.x,
    y: firstEnd.y - firstStart.y,
  }
  const secondVector = {
    x: secondEnd.x - secondStart.x,
    y: secondEnd.y - secondStart.y,
  }
  const denominator = cross(firstVector, secondVector)
  if (Math.abs(denominator) <= GEOMETRY_EPSILON) return null
  const betweenStarts = {
    x: secondStart.x - firstStart.x,
    y: secondStart.y - firstStart.y,
  }
  const firstProgress = cross(betweenStarts, secondVector) / denominator
  const secondProgress = cross(betweenStarts, firstVector) / denominator
  if (
    firstProgress < -GEOMETRY_EPSILON ||
    firstProgress > 1 + GEOMETRY_EPSILON ||
    secondProgress < -GEOMETRY_EPSILON ||
    secondProgress > 1 + GEOMETRY_EPSILON
  ) {
    return null
  }
  return { firstProgress, secondProgress }
}

function isSimplePolygon(points: readonly CenteringBoundaryPoint[]) {
  if (points.length < 3 || points.some((point) => !finitePoint(point))) return false
  for (let firstIndex = 0; firstIndex < points.length; firstIndex += 1) {
    const firstNext = (firstIndex + 1) % points.length
    for (let secondIndex = firstIndex + 1; secondIndex < points.length; secondIndex += 1) {
      const secondNext = (secondIndex + 1) % points.length
      if (firstIndex === secondIndex || firstNext === secondIndex || secondNext === firstIndex) {
        continue
      }
      if (
        intersectLineSegments(
          points[firstIndex],
          points[firstNext],
          points[secondIndex],
          points[secondNext],
        )
      ) {
        return false
      }
    }
  }
  return true
}

function isConvexQuadrilateral(points: CenteringOuterBoundary['points']) {
  const turns = points.map((point, index) => {
    const next = points[(index + 1) % points.length]
    const afterNext = points[(index + 2) % points.length]
    return cross(
      { x: next.x - point.x, y: next.y - point.y },
      { x: afterNext.x - next.x, y: afterNext.y - next.y },
    )
  })
  if (turns.some((turn) => !finiteNumber(turn) || Math.abs(turn) < GEOMETRY_EPSILON)) {
    return false
  }
  const direction = Math.sign(turns[0])
  return turns.every((turn) => Math.sign(turn) === direction)
}

function lineFromPoints(
  first: CenteringBoundaryPoint,
  second: CenteringBoundaryPoint,
): CenteringCurveLike {
  return {
    x1: first.x,
    y1: first.y,
    x2: second.x,
    y2: second.y,
  }
}

function pointOnSegment(
  point: CenteringBoundaryPoint,
  start: CenteringBoundaryPoint,
  end: CenteringBoundaryPoint,
) {
  const segment = { x: end.x - start.x, y: end.y - start.y }
  const toPoint = { x: point.x - start.x, y: point.y - start.y }
  if (Math.abs(cross(segment, toPoint)) > 1e-7) return false
  return (
    point.x >= Math.min(start.x, end.x) - GEOMETRY_EPSILON &&
    point.x <= Math.max(start.x, end.x) + GEOMETRY_EPSILON &&
    point.y >= Math.min(start.y, end.y) - GEOMETRY_EPSILON &&
    point.y <= Math.max(start.y, end.y) + GEOMETRY_EPSILON
  )
}

function pointsEqual(first: CenteringBoundaryPoint, second: CenteringBoundaryPoint) {
  return (
    Math.abs(first.x - second.x) <= GEOMETRY_EPSILON &&
    Math.abs(first.y - second.y) <= GEOMETRY_EPSILON
  )
}

function cross(first: CenteringBoundaryPoint, second: CenteringBoundaryPoint) {
  return first.x * second.y - first.y * second.x
}

function finitePoint(point: CenteringBoundaryPoint) {
  return finiteNumber(point.x) && finiteNumber(point.y)
}

function pointInImage(point: CenteringBoundaryPoint, width: number, height: number) {
  return point.x >= 0 && point.x <= width - 1 && point.y >= 0 && point.y <= height - 1
}

function segmentIntersectsImage(
  start: CenteringBoundaryPoint,
  end: CenteringBoundaryPoint,
  width: number,
  height: number,
) {
  const deltaX = end.x - start.x
  const deltaY = end.y - start.y
  let minimumParameter = 0
  let maximumParameter = 1

  for (const [direction, distance] of [
    [-deltaX, start.x],
    [deltaX, width - 1 - start.x],
    [-deltaY, start.y],
    [deltaY, height - 1 - start.y],
  ] as const) {
    if (Math.abs(direction) <= GEOMETRY_EPSILON) {
      if (distance < 0) return false
      continue
    }
    const parameter = distance / direction
    if (direction < 0) {
      minimumParameter = Math.max(minimumParameter, parameter)
    } else {
      maximumParameter = Math.min(maximumParameter, parameter)
    }
    if (minimumParameter > maximumParameter) return false
  }
  return true
}

function finiteNumber(value: number) {
  return Number.isFinite(value)
}

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(Math.max(value, minimum), maximum)
}
